import { useSelector } from 'react-redux';
import { Button, MixerLoader, Modal, YesNoModalWithReason } from '../../components/Common';
import EvaluationTable from '../../components/Evaluation/EvaluationTable';
import { RootState } from '../../store';
import { EvaluationFilterDto, EvaluationSortBy, UserDto } from '../../commonTypes/user.types';
import { useNavigate } from 'react-router';
import { useEffect, useState } from 'react';
import { useHttpGetAllEvaluationsOnMount } from '../../hooks';
import useHttpRequestEvaluationUpdate from '../../hooks/user/userApi/useHttpRequestEvaluationUpdate';
import useHttpApproveOrRejectEvaluationUpdate from '../../hooks/user/userApi/useHttpApproveOrRejectEvaluationUpdate';
import { EvaluationStatus } from '../../commonTypes/evaluation/calculate.types';
import { ExpandableSearch } from '../../components/Common/ExpandableSearch';
import { Formik } from 'formik';
import { useTranslation } from '../../hooks/useTranslation';
import { useResizeListener } from '../../hooks/windowEvents/useResizeListener';
import NotAvailableOnMobile from '../../components/Common/NotAvailableOnMobile';
import EmptyScreen from '../../components/Evaluation/EmptyScreen';

interface FormValues {
    search: string;
}

type CanBeRequestedStatus = EvaluationStatus.UPDATE_APPROVED | EvaluationStatus.SUBMITTED;

const Evaluation = () => {
    const { t } = useTranslation('evaluations');
    const navigate = useNavigate();
    const isMobile = useResizeListener().isMaxWidthLarge;
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [evaluationId, setEvaluationId] = useState<string>('');
    const user = useSelector<RootState, UserDto>(state => state.auth.value);
    const isAdminOwner = user.role.name === 'ADMIN' || user.role.name === 'OWNER';
    const [activeTab, setActiveTab] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState<EvaluationSortBy>(EvaluationSortBy.EVALUATOR);
    const [sortDirection, setSortDirection] = useState<'DESC' | 'ASC'>('DESC');
    const [updateReason, setUpdateReason] = useState<string>('');
    const baseTabs = [
        { label: 'Requested Evaluations', status: EvaluationStatus.REQUESTED },
        { label: 'Completed Evaluations', status: EvaluationStatus.SUBMITTED },
    ];
    const tabs = isAdminOwner ? [...baseTabs, { label: 'Update Requests', status: EvaluationStatus.UPDATE_REQUESTED }] : baseTabs;
    const { exec } = useHttpRequestEvaluationUpdate();
    const [refresh, setRefresh] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [page, setPage] = useState(0);
    const [filter, setFilter] = useState<EvaluationFilterDto>({
        status: tabs[activeTab].status,
        sortBy: sortBy,
        sortDirection: sortDirection,
        page: page,
        pageSize: perPage,
    });

    const { isReady, evaluations, total } = useHttpGetAllEvaluationsOnMount(refresh, filter);
    const { exec: approveOrRejectExec } = useHttpApproveOrRejectEvaluationUpdate();

    useEffect(() => {
        setFilter(prev => ({
            ...prev,
            sortBy: sortBy,
            sortDirection: sortDirection,
        }));
    }, [sortBy, sortDirection]);

    useEffect(() => {
        setPage(0);
        setFilter(prev => ({
            ...prev,
            page: 0,
        }));
    }, [activeTab]);

    const handleApproveOrReject = (id: string, status: CanBeRequestedStatus) => {
        approveOrRejectExec(id, status, setRefresh);
    };

    const TAB_CONFIG = {
        0: {
            button: {
                buttonType: 'perform',
                buttonType2: undefined,
                handleApproveOrReject: undefined,
            },
            emptyScreen: {
                title: isAdminOwner ? t('requestedEmptyScreenTitlePrivileged') : t('requestedEmptyScreenTitle'),
                paragraph: isAdminOwner ? t('requestedEmptyScreenParagraphPrivileged') : t('requestedEmptyScreenParagraph'),
                buttonText: isAdminOwner ? t('addNew') : undefined,
                onClick: () => navigate('/create-evaluation'),
                img: '/svg/empty-screen-illustration.svg',
            },
        },
        1: {
            button: { buttonType: 'edit', buttonType2: undefined, handleApproveOrReject: undefined, setIsModalVisible, setEvaluationId },
            emptyScreen: {
                title: t('completedEmptyScreenTitle'),
                paragraph: isAdminOwner ? t('completedEmptyScreenParagraphPrivileged') : t('completedEmptyScreenParagraph'),
                img: '/svg/empty-screen-illustration-completed.svg',
            },
        },
        2: {
            button: { buttonType: 'approve', buttonType2: 'reject', handleApproveOrReject },
            emptyScreen: {
                title: t('updateRequestedEmptyScreenTitle'),
                paragraph: t('updateRequestedEmptyScreenParagraph'),
                img: '/svg/empty-screen-illustration-update.svg',
            },
        },
    } as const;

    const config = TAB_CONFIG[activeTab as keyof typeof TAB_CONFIG];

    if (!isReady)
        return (
            <div className="mixer-loader__container">
                <MixerLoader position={'centerOnParent'} />;
            </div>
        );

    const getTable = () => {
        const { buttonType, buttonType2, handleApproveOrReject } = config.button;

        return (
            <EvaluationTable
                activeTab={activeTab}
                setSortBy={setSortBy}
                setSortDirection={setSortDirection}
                sortBy={sortBy}
                sortDirection={sortDirection}
                searchTerm={searchTerm}
                isReady={isReady}
                tableInfo={evaluations}
                buttonType={buttonType}
                buttonType2={buttonType2}
                handleApproveOrReject={handleApproveOrReject}
                setIsModalVisible={setIsModalVisible}
                setEvaluationId={setEvaluationId}
                pagination={{
                    total: total,
                    perPage: perPage,
                    setPerPage: newPerPage => {
                        setPerPage(newPerPage);
                        setFilter({ ...filter, pageSize: newPerPage as number });
                    },
                    page: page,
                    handlePageChange: newPage => {
                        setPage(newPage);
                        setFilter({ ...filter, page: newPage as number });
                    },
                }}
            />
        );
    };

    const getEmptyScreen = () => {
        const emptyScreen = config.emptyScreen;
        return <EmptyScreen {...emptyScreen} />;
    };

    return isMobile ? (
        <NotAvailableOnMobile title={t('notAvailableOnMobileTitle')} paragraph={t('notAvailableOnMobileParagraph')} />
    ) : (
        <main>
            <Formik<FormValues> initialValues={{ search: '' }} onSubmit={() => {}}>
                {formik => (
                    <div>
                        <div className="evaluations__tab__container">
                            {tabs.map((tab, index) => (
                                <button
                                    key={index}
                                    onClick={() => {
                                        setActiveTab(index);
                                        setFilter({ ...filter, status: tab.status });
                                    }}
                                    className={`${activeTab === index && 'evaluations__tab__button--active'} evaluations__tab__button`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                        {isReady && evaluations.length < 1 && !searchTerm ? (
                            getEmptyScreen()
                        ) : (
                            <div className="evaluations">
                                <div className="evaluations__header">
                                    <h1 className="evaluations__title">{tabs[activeTab].label}</h1>
                                    <div className="evaluations__search-container">
                                        <ExpandableSearch
                                            defaultIsExpanded={!isAdminOwner}
                                            id="search"
                                            placeholder={t('search')}
                                            context={formik}
                                            className="invoices__search"
                                            setSearchTerm={setSearchTerm}
                                            isCloseButtonVisible={formik.values.search !== ''}
                                            closeButtonOnClick={() => {
                                                formik.setFieldValue('search', '');
                                                setSearchTerm('');
                                            }}
                                        />
                                        {isAdminOwner && (
                                            <Button
                                                onClick={() => {
                                                    navigate('/create-evaluation');
                                                }}
                                                text={t('addNew')}
                                            />
                                        )}
                                    </div>
                                </div>
                                <div>{getTable()}</div>
                            </div>
                        )}
                    </div>
                )}
            </Formik>
            {isModalVisible && (
                <Modal
                    isVisible={isModalVisible}
                    closeModal={() => {
                        setIsModalVisible(false);
                        setEvaluationId('');
                        setUpdateReason('');
                    }}
                >
                    <YesNoModalWithReason
                        placeholder={t('updateReasonPlaceholder')}
                        updateReason={updateReason}
                        setUpdateReason={setUpdateReason}
                        title={t('updateModalTitle')}
                        description={t('updateModalDescription')}
                        onYes={() => {
                            setIsModalVisible(false);
                            exec(evaluationId, updateReason, setRefresh);
                            setEvaluationId('');
                            setUpdateReason('');
                        }}
                        onNo={() => {
                            setIsModalVisible(false);
                            setEvaluationId('');
                            setUpdateReason('');
                        }}
                        onYesText={t('updateModalAccept')}
                        onNoText={t('updateModalReject')}
                    />
                </Modal>
            )}
        </main>
    );
};

export default Evaluation;
